<template>
    <v-form v-if="userProfile">
        <v-container v-if="!userProfile.emailConfirmed" class="pt-8">
            <v-row v-if="confirmationSent">
                Confirmation sent. Please check your email account and refresh this page once you have confirmed your
                email to edit your profile.
            </v-row>
            <v-row v-else>
                <v-col>
                    Your email address is not confirmed. You should have recieved a confirmation email when you created
                    your account. You cannot edit your profile until it is confirmed. Please click the button below to
                    resend the confrimation email
                    <v-btn @click="resendConfirmation">Resend Confirmation</v-btn>
                </v-col>
            </v-row>
        </v-container>
        <v-container v-else>
            <v-row v-if="errorMessage" justify>
                <v-col>
                    <v-alert type="error">
                        {{ errorMessage }}
                    </v-alert>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6" order-md="2">
                    <div class="d-flex justify-center justify-md-end">
                        <action-buttons
                            state="Edit"
                            :permissions="permissions"
                            :disabled="disableButtons"
                            @save="saveChanges"
                            @cancel="handleCancel"
                            @close="handleClose"
                            :is-dirty="isDirty"
                            class="d-flex justify-end"
                        />
                    </div>
                </v-col>
                <v-col cols="12" md="6" order-md="1">
                    <div class="text-h5">Edit your profile!!!</div>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-text-field v-model="userProfile.firstName" label="First Name" :rules="[requiredRule]"></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field v-model="userProfile.lastName" label="Last Name" :rules="[requiredRule]"></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-text-field v-model="userProfile.displayName" label="Display Name"></v-text-field>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <div class="text-subtitle-1">Email Frequency</div>
                    <v-radio-group v-model="emailFrequency" row>
                        <v-radio label="None" value="0"></v-radio>
                        <v-radio label="Daily" value="1"></v-radio>
                        <v-radio label="Hourly" value="2"></v-radio>
                    </v-radio-group>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-color-picker v-model="backgroundHexColor" mode="hexa" hide-mode-switch></v-color-picker>
                </v-col>
                <v-col cols="12" md="6">
                    <v-avatar :color="iconColour" size="40">
                        <v-icon v-if="userProfile.icon">
                            {{ userProfile.icon }}
                        </v-icon>
                        <span v-else :class="fontColour + `--text`">{{ initials }}</span>
                    </v-avatar>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>
<script>
import api from '@/api'
import navGuardConfirmMixin from '../../mixins/navGuardConfirmMixin'
import editorMixin from '../../mixins/editorMixin'
import permissionsSet from '../../components/permissions'
export default {
    name: 'UserProfileEditor',
    mixins: [navGuardConfirmMixin, editorMixin],
    data() {
        return {
            errorMessage: '',
            requiredRule: v => !!v || 'Required',
            userProfile: {
                emailFrequency: '1',
            },
            emailFrequency: '1',
            confirmationSent: false,
            permissions: [permissionsSet.Edit],
            backgroundHexColor: '#000000',
            isSaving: false,
        }
    },
    computed: {
        readOnly() {
            return this.userProfile ? this.userProfile.isAccountDisabled : true
        },
        initials() {
            let initials = 'AB'
            if (this.userProfile.firstName.length > 0) {
                initials = `${this.userProfile.firstName[0]}${initials[1]}`
            }
            if (this.userProfile.lastName.length > 0) {
                initials = `${initials[0]}${this.userProfile.lastName[0]}`
            }
            return initials
        },
        iconColour() {
            return this.backgroundHexColor
        },
        fontColour() {
            let hexcolor = this.backgroundHexColor.replace('#', '')
            const [r, g, b] = [0, 2, 4].map(p => parseInt(hexcolor.substr(p, 2), 16))
            return (r * 299 + g * 587 + b * 114) / 1000 >= 128 ? 'black' : 'white'
        },
        disableButtons() {
            return this.isLoading || this.isSaving
        },
    },
    watch: {
        userProfile: {
            deep: true,
            handler() {
                this.isDirty = true
            },
        },
        backgroundHexColor(newVal) {
            if (newVal) this.userProfile.iconColour = newVal
        },
        emailFrequency(newVal) {
            if (newVal) this.userProfile.emailFrequency = newVal
        },
    },
    mounted() {
        this.getUser()
    },
    methods: {
        getUser() {
            this.isLoading = true
            api.get(`user/getProfile`)
                .then(res => {
                    this.userProfile = res
                    if (this.userProfile.iconColour) {
                        this.backgroundHexColor = this.userProfile.iconColour
                    }
                    if (this.userProfile.emailFrequency) {
                        this.emailFrequency = `${this.userProfile.emailFrequency}`
                    }
                })
                .finally(() => {
                    this.isDirty = false
                    this.isLoading = false
                })
        },
        saveChanges() {
            this.isSaving = true
            api.post(`user/updateUserProfile`, this.userProfile)
                .then(() => {
                    this.showSnackbar('info', 'Profile Updated')
                    this.isDirty = false
                    window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        handleCancel() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        handleClose() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        resendConfirmation() {
            this.isSaving = true
            api.post('user/resendConfirmation')
                .then(() => {
                    this.confirmationSent = true
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
    },
}
</script>
